import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiMessage, UserMessage } from "@/zapi-frontend/shared/ui/chat";
import type { TSetupProgress } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import { formatMinutesLeft, setupMinutesLeft } from "@/zapi-frontend/shared/lib/setupProgress";
import { LeoWelcome } from "./LeoWelcome";

const WAITING_ASKS = ["ready", "deliverables", "change"] as const;
type TWaitingAsk = (typeof WAITING_ASKS)[number];

type TWaitingExchange = { question: string; answer: string };

// Leo's side of the chat while the analysis is set up: canned answers, nothing reaches the backend.
export function useAnalysisWaiting(projectId: string, isWaiting: boolean, setup: TSetupProgress | null) {
  const { t } = useTranslation("projectHome");
  const [exchanges, setExchanges] = useState<TWaitingExchange[]>([]);

  useEffect(() => {
    setExchanges([]);
  }, [projectId, isWaiting]);

  const answerTo = (ask: TWaitingAsk) => {
    if (ask !== "ready") return t(`leo.waiting.answer.${ask}`);
    const minutesLeft = setupMinutesLeft(setup, Date.now());
    return minutesLeft > 0
      ? t("leo.waiting.answer.ready", { eta: formatMinutesLeft(t, minutesLeft) })
      : t("leo.waiting.answer.readySoon");
  };

  return {
    exchanges,
    ask: (ask: TWaitingAsk) =>
      setExchanges((current) => [...current, { question: t(`leo.waiting.ask.${ask}`), answer: answerTo(ask) }]),
    reply: (text: string) =>
      setExchanges((current) => [...current, { question: text, answer: t("leo.waiting.answer.locked") }])
  };
}

type TAnalysisWaitingProps = {
  exchanges: TWaitingExchange[];
  onAsk: (ask: TWaitingAsk) => void;
};

export function AnalysisWaiting({ exchanges, onAsk }: TAnalysisWaitingProps) {
  const { t } = useTranslation("projectHome");

  if (exchanges.length === 0) {
    const asks = WAITING_ASKS.map((ask) => ({ ask, label: t(`leo.waiting.ask.${ask}`) }));
    return (
      <LeoWelcome
        title={t("leo.waiting.title")}
        subtitle={t("leo.waiting.subtitle")}
        suggestions={asks.map(({ label }) => label)}
        onPick={(label) => {
          const picked = asks.find((item) => item.label === label);
          if (picked) onAsk(picked.ask);
        }}
      />
    );
  }

  return (
    <>
      <AiMessage className="w-full">
        <p>{t("leo.waiting.subtitle")}</p>
      </AiMessage>
      {exchanges.map(({ question, answer }, index) => (
        <div key={index} className="flex flex-col gap-6">
          <UserMessage>
            <span className="whitespace-pre-wrap">{question}</span>
          </UserMessage>
          <AiMessage className="w-full">
            <p>{answer}</p>
          </AiMessage>
        </div>
      ))}
    </>
  );
}
